import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import Breadcrumbs from "../../../components/BreadCrumbs";
import EmptyState from "../../../components/EmptyState";
import Avatar from "../../../components/Avatar";
import TruncatedWithTooltip from "../../../components/TruncatedWithTooltip";
import Filters from "../../../components/Filter";
import employeesPlaceholder from "../../../assets/images/employees/placeholder.png";
import messages from "../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import { textContainOnlySpaces } from "../../../utils/Helpers";
import {
  isAdmin,
  isLoginUser,
  isSuperAdmin,
  isVisitor,
  isSupervisorManger,
} from "../../../utils/Permissions";
import { useAdminModulePermission } from "../../../utils/adminPortalHelpers";
import { adminPermissionModules } from "../../../utils/Constants";
import "./Employees.scss";
import {
  UICard,
  UIButton,
  UIModal,
  UIPaginationBar,
  UIRowActionsMenu,
  UITable,
} from "../../../components/ui";
import {
  initialFetchEmployee,
  setEmployeePage,
  setEmployeeRowCount,
  setEmployeeSearch,
} from "../../../store/PersistEmployees/actions";
import { deleteEmployeeRequest } from "../../../store/Employees/actions";
import { getPermissionRequest } from "../../../store/Auth/actions";
import store from "../../../store";

const Employees = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const lang = useSelector((state) => state.locale.lang);
  const isLoading = useSelector((state) => state.loader);
  const meta = useSelector((state) => state.employees.meta);
  const list = useSelector((state) => state.employees.list);
  const { employees, shared } = messages[lang];
  const persist = useSelector((state) => state.persistEmployee);

  const [q, setQuery] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);

  const { id } = store.getState().auth.login.role;
  const { canAdd, canEdit, canDelete } = useAdminModulePermission(
    adminPermissionModules.employees,
  );

  const pageSize = persist.rowCount || 10;
  const currentPage = meta?.currentPage || persist.page || 1;

  const handlePageChange = (newPage) => {
    dispatch(setEmployeePage(newPage));
  };

  const handlePageSizeChange = (nextSize) => {
    dispatch(setEmployeeRowCount(nextSize));
  };

  useEffect(() => {
    if ((q.length >= 3 || q.length === 0) && !textContainOnlySpaces(q)) {
      dispatch(setEmployeeSearch(q));
    }
  }, [q, dispatch]);

  useEffect(() => {
    dispatch(initialFetchEmployee());
  }, [lang, persist.page, persist.searchValue, persist.rowCount, dispatch]);

  useEffect(() => {
    if (
      location.pathname === ROUTE_PATHS.employees &&
      !isSuperAdmin() &&
      !isLoginUser() &&
      !isVisitor()
    ) {
      history.push(ROUTE_PATHS.profile);
    }
  }, [location, history]);

  useEffect(() => {
    if (!isSuperAdmin() && !isAdmin() && !isSupervisorManger()) {
      dispatch(getPermissionRequest({ id }));
    }
  }, [lang, dispatch, id]);

  const handleEditEmployee = useCallback(
    (event, employee) => {
      event.stopPropagation();
      history.push(ROUTE_PATHS.editEmployees.replace(":id", employee.id));
    },
    [history]
  );

  const handleDeleteEmployee = useCallback((event, employee) => {
    event.stopPropagation();
    setEmployeeToDelete(employee);
    setDeleteModalOpen(true);
  }, []);

  const confirmDeleteEmployee = () => {
    if (employeeToDelete?.id) {
      dispatch(deleteEmployeeRequest(employeeToDelete.id));
    }
    setDeleteModalOpen(false);
    setEmployeeToDelete(null);
  };

  const cancelDeleteEmployee = () => {
    setDeleteModalOpen(false);
    setEmployeeToDelete(null);
  };

  const navigateToEmployee = useCallback(
    (row) => {
      history.push(ROUTE_PATHS.viewEmployee.replace(":id", row.id));
    },
    [history]
  );

  const rows = useMemo(
    () =>
      (list || []).map((row) => ({
        ...row,
        id: row.id,
      })),
    [list]
  );

  const columns = useMemo(() => {
    const cols = [
      {
        field: "image",
        headerName: shared.image,
        width: 72,
        flex: 0,
        sortable: false,
        renderCell: (params) => (
          <Avatar
            imgSrcAlt="employee name"
            imgSrc={params.row?.image_url}
            name={params.row?.name}
            size={30}
          />
        ),
      },
      {
        field: "name",
        headerName: employees.listEmployees.name,
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: (params) => {
          const value = params.value || "-";
          if (value === "-") {
            return <span className="MuiDataGrid-cellContent">-</span>;
          }
          return (
            <TruncatedWithTooltip title={value}>
              <span className="MuiDataGrid-cellContent">{value}</span>
            </TruncatedWithTooltip>
          );
        },
      },
      {
        field: "email",
        headerName: employees.listEmployees.email,
        flex: 1.2,
        minWidth: 180,
        sortable: false,
        renderCell: (params) => {
          const value = params.value || "-";
          if (value === "-") {
            return <span className="MuiDataGrid-cellContent">-</span>;
          }
          return (
            <TruncatedWithTooltip title={value}>
              <span className="MuiDataGrid-cellContent">{value}</span>
            </TruncatedWithTooltip>
          );
        },
      },
      {
        field: "phone",
        headerName: employees.listEmployees.mobile,
        flex: 0.9,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => {
          const { phone_number: phoneNumber, dial_code: dialCode } =
            params.row || {};
          return phoneNumber ? (
            <span className="employees-list-view__phone">
              {`${dialCode || ""} ${phoneNumber}`.trim()}
            </span>
          ) : (
            "-"
          );
        },
      },
      {
        field: "role",
        headerName: employees.listEmployees.jobTitle,
        flex: 0.8,
        minWidth: 120,
        sortable: false,
        valueGetter: (params) => params.row.role?.name || "-",
      },
    ];

    if (canEdit || canDelete) {
      cols.push({
        field: "actions",
        headerName: employees.listEmployees.actions,
        width: 90,
        minWidth: 80,
        flex: 0,
        sortable: false,
        align: "center",
        headerAlign: "center",
        cellClassName: "employees-list-view__actions-cell",
        renderCell: (params) => {
          const items = [];
          const stopRowClick = { stopPropagation: () => {} };

          if (canEdit) {
            items.push({
              key: "edit",
              label: shared.edit,
              onClick: () => handleEditEmployee(stopRowClick, params.row),
            });
          }

          if (canDelete) {
            items.push({
              key: "delete",
              label: employees.deleteEmployee,
              danger: true,
              onClick: () => handleDeleteEmployee(stopRowClick, params.row),
            });
          }

          return (
            <UIRowActionsMenu
              ariaLabel={employees.listEmployees.actions}
              items={items}
            />
          );
        },
      });
    }

    return cols;
  }, [
    shared,
    employees,
    canEdit,
    canDelete,
    handleEditEmployee,
    handleDeleteEmployee,
  ]);

  const paginationFrom =
    meta?.count > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const paginationTo = Math.min(currentPage * pageSize, meta?.count || 0);
  const paginationPages = meta?.pages || 1;

  const renderEmployeesList = () => {
    if (!isLoading && !rows.length) {
      return (
        <UICard>
          <EmptyState
            description={employees.noDataDescription}
            imgSrc={employeesPlaceholder}
            showActionButton={false}
          />
        </UICard>
      );
    }

    return (
      <>
        <UITable
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          contentSized
          hideFooter
          disableSelectionOnClick
          onRowClick={({ row }) => navigateToEmployee(row)}
        />
        {(meta?.count || 0) > 0 && (
          <UIPaginationBar
            currentPage={currentPage}
            pages={paginationPages}
            pageSize={pageSize}
            rangeTemplate={employees.showingRange}
            rangeFrom={paginationFrom}
            rangeTo={paginationTo}
            rangeTotal={meta?.count || 0}
            rowsPerPageLabel={shared.numberOfRows}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            className="employees-list-view__pagination"
          />
        )}
      </>
    );
  };

  return (
    <div className="employees-list-view">
      <div className="page-title-margin">
        <Breadcrumbs
          list={[
            {
              path: "",
              label: employees.employeesList,
            },
          ]}
          BreadCrumbHasBackIcon={false}
        />
        <Box className="d-flex justify-content-between align-items-center">
          <Typography variant="h5">{employees.employeesList}</Typography>
          {canAdd && (
            <UIButton
              appearance="gradient"
              type="button"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => history.push(ROUTE_PATHS.addProjectManager)}
            >
              {employees.addProjectManager.addEmployee}
            </UIButton>
          )}
        </Box>
      </div>

      <Filters
        searchInput={{
          placeholder: employees.searchPlaceholder,
          name: "search",
          value: q,
          onChange: (value) => setQuery(value),
        }}
        filters={[]}
        classNames={{
          container: "employees-list-view__filters",
          base: "",
          search: "employees-list-view__search mb-3 mb-md-0",
          filter: "",
        }}
      />

      <div className="employees-list-view__content d-flex flex-column justify-content-between">
        {renderEmployeesList()}
      </div>

      <UIModal
        open={deleteModalOpen}
        onClose={cancelDeleteEmployee}
        title={employees.deleteEmployeeTitle}
        maxWidth="sm"
        showClose={false}
        actions={
          <Stack
            direction="row"
            spacing={2}
            width="100%"
            className="employees-list-view__delete-actions"
          >
            <UIButton
              variant="outlined"
              color="inherit"
              fullWidth
              onClick={cancelDeleteEmployee}
            >
              {employees.deleteModalNo}
            </UIButton>
            <UIButton
              appearance="gradient"
              fullWidth
              onClick={confirmDeleteEmployee}
            >
              {employees.deleteModalYes}
            </UIButton>
          </Stack>
        }
      >
        <Typography className="employees-list-view__delete-message">
          {employees.deleteEmployeeConfirm}
        </Typography>
      </UIModal>
    </div>
  );
};

export default Employees;
